import {
    ALL_ANIMATED_MIME_TYPES,
    ALL_MIME_TYPES,
    FONT_MIME_TYPES,
    MAX_FILE_COUNT,
    MAX_FILE_DURATION,
    MAX_FILE_SIZE,
    MAX_OUTPUT_VIDEO_ASPECT_RATIO,
    MAX_OUTPUT_VIDEO_DURATION,
    MIN_OUTPUT_VIDEO_ASPECT_RATIO,
    MIN_OUTPUT_VIDEO_DIMENSION,
    MIN_OUTPUT_VIDEO_DURATION,
    STILL_MIME_TYPES,
    VIDEO_MIME_TYPES,
} from '../constants'
import getLabelFromMimeTypes from '../utils/get-mime-types-label'
import { fileSizeToDisplayText, secondsToDisplayText } from '../utils/text-utils'
import CreationToolErrorFix from './error-fix'
import CreationToolOutputType from './output-type'

enum CreationToolError {
    InvalidMediaFile = 'CT_ERROR_INVALID_MEDIA_FILE',
    InvalidVideoFile = 'CT_ERROR_INVALID_VIDEO_FILE',
    InvalidStillFile = 'CT_ERROR_INVALID_STILL_FILE',
    InvalidAnimatedFile = 'CT_ERROR_INVALID_ANIMATED_FILE',
    InvalidFontFile = 'CT_ERROR_INVALID_FONT_FILE',
    InvalidVideoUrl = 'CT_ERROR_INVALID_VIDEO_URL',
    FileSizeTooLarge = 'CT_ERROR_FILE_SIZE_TOO_LARGE',
    VideoTooShort = 'CT_ERROR_VIDEO_TOO_SHORT',
    VideoTooLong = 'CT_ERROR_VIDEO_TOO_LONG',
    VideoWidthTooSmall = 'CT_ERROR_VIDEO_WIDTH_TOO_SMALL',
    InvalidMOVFile = 'CT_ERROR_INVALID_MOV_FILE',
    VideoHeightTooSmall = 'CT_ERROR_VIDEO_HEIGHT_TOO_SMALL',
    VideoAspectRatioTooSmall = 'CT_ERROR_VIDEO_ASPECT_RATIO_TOO_SMALL',
    VideoAspectRatioTooBig = 'CT_ERROR_VIDEO_ASPECT_RATIO_TOO_BIG',
    VideoPrivate = 'CT_ERROR_VIDEO_PRIVATE',
    VideoLiveStream = 'CT_ERROR_VIDEO_LIVE_STREAM',
    VideoNeedsTrim = 'CT_ERROR_VIDEO_NEEDS_TRIM',
    VideoTrimError = 'CT_ERROR_VIDEO_TRIM_ERROR',
    NoAudio = 'CT_ERROR_NO_AUDIO',
    NoSuperCutFiles = 'CT_ERROR_NO_SUPERCUT_FILES',
    NoTransparency = 'CT_ERROR_NO_TRANSPARENCY',
    NotAnimated = 'CT_ERROR_NOT_ANIMATED',
    UploadFailed = 'CT_ERROR_UPLOAD_FAILED',
    UploadFailedAgain = 'CT_ERROR_UPLOAD_FAILED_AGAIN',
    SuperCutLoadFailed = 'CT_ERROR_SUPER_CUT_LOAD_FAILED',
    SuperCutUploadFailed = 'CT_ERROR_SUPER_CUT_UPLOAD_FAILED',
    SuperCutUpdateFailed = 'CT_ERROR_SUPER_CUT_UPDATE_FAILED',
    TooManyFiles = 'CT_ERROR_TOO_MANY_FILES',
    CaptionDownloadFailed = 'CT_ERROR_CAPTION_DOWNLOAD_FAILED',
    RemoveBackgroundFailed = 'CT_ERROR_REMOVE_BACKGROUND_FAILED',
}

namespace CreationToolError {
    /**
     * Returns the error's display title.
     */
    export function title(error: CreationToolError): string {
        switch (error) {
            case CreationToolError.InvalidVideoFile:
            case CreationToolError.InvalidStillFile:
            case CreationToolError.InvalidAnimatedFile:
            case CreationToolError.InvalidMediaFile:
                return `Invalid file type!`
            case CreationToolError.InvalidVideoUrl:
                return `Invalid video URL!`
            case CreationToolError.FileSizeTooLarge:
                return `This file is too large!`
            case CreationToolError.VideoTooShort:
                return `This video is too short!`
            case CreationToolError.VideoTooLong:
            case CreationToolError.VideoNeedsTrim:
                return `This video is too long!`
            case CreationToolError.VideoWidthTooSmall:
            case CreationToolError.VideoHeightTooSmall:
                return `This video is too small!`
            case CreationToolError.VideoAspectRatioTooSmall:
            case CreationToolError.VideoAspectRatioTooBig:
                return `Invalid video dimensions!`
            case CreationToolError.VideoPrivate:
                return `This video is private!`
            case CreationToolError.VideoLiveStream:
                return `This video may be a live stream!`
            case CreationToolError.VideoTrimError:
                return `Trimming failed!`
            case CreationToolError.NoSuperCutFiles:
                return `No files found!`
            case CreationToolError.NoAudio:
                return `No audio detected!`
            case CreationToolError.NoTransparency:
                return `No transparency detected!`
            case CreationToolError.NotAnimated:
                return `No animation detected!`
            case CreationToolError.UploadFailed:
            case CreationToolError.UploadFailedAgain:
                return `Upload failed!`
            case CreationToolError.SuperCutUploadFailed:
                return `Unable to cut this file!`
            case CreationToolError.SuperCutUpdateFailed:
                return `Unable to update this capture!`
            case CreationToolError.SuperCutLoadFailed:
                return `Unable to load captures.`
            case CreationToolError.TooManyFiles:
                return `Too many files!`
            case CreationToolError.InvalidFontFile:
                return 'This file type is invalid!'
            case CreationToolError.InvalidMOVFile:
                return `This file isn't supported on this browser!`
            case CreationToolError.CaptionDownloadFailed:
                return `Unable to download caption data!`
            case CreationToolError.RemoveBackgroundFailed:
                return `Unable to remove the background.`
        }
    }

    export function gaValue(error: CreationToolError, file: File | string): string | undefined {
        if (file instanceof File) {
            switch (error) {
                case CreationToolError.FileSizeTooLarge:
                    return String(file.size)
            }
        }
    }

    export function gaAction(error: CreationToolError, outputType: CreationToolOutputType): string {
        return `${gaActionText(error)} - ${CreationToolOutputType.title(outputType)}`
    }

    /**
     * Returns the error's ga label
     */
    export function gaLabel(error: CreationToolError, file: File | string): string {
        if (file instanceof File) {
            switch (error) {
                case CreationToolError.InvalidVideoFile:
                case CreationToolError.NotAnimated:
                case CreationToolError.NoAudio:
                case CreationToolError.NoTransparency:
                case CreationToolError.FileSizeTooLarge:
                case CreationToolError.InvalidStillFile:
                case CreationToolError.InvalidAnimatedFile:
                case CreationToolError.InvalidMediaFile:
                    return String(file.type)
                case CreationToolError.VideoAspectRatioTooSmall:
                    return `too small`
                case CreationToolError.VideoAspectRatioTooBig:
                    return `too big`
            }
        }
        switch (error) {
            case CreationToolError.InvalidVideoUrl:
                return file as string
        }
        return 'Error Code Not Found'
    }

    /**
     * Returns the error's ga label
     */
    function gaActionText(error: CreationToolError): string {
        switch (error) {
            case CreationToolError.InvalidVideoFile:
                return `Error Invalid Format`
            case CreationToolError.NotAnimated:
                return 'Error No Animation'
            case CreationToolError.NoAudio:
                return 'Error No Audio'
            case CreationToolError.NoTransparency:
                return `Error No Transparency`
            case CreationToolError.FileSizeTooLarge:
                return `Error Too Large`
            case CreationToolError.VideoAspectRatioTooSmall:
            case CreationToolError.VideoAspectRatioTooBig:
                return `Error Invalid Aspect Ratio`
            case CreationToolError.InvalidVideoUrl:
                return `Error Invalid URL`
            case CreationToolError.VideoTrimError:
                return `Error Trimmer`
            case CreationToolError.InvalidStillFile:
            case CreationToolError.InvalidAnimatedFile:
            case CreationToolError.InvalidMediaFile:
                return `Error Invalid Format`
            case CreationToolError.VideoTooShort:
                return `Error Too Short`
            case CreationToolError.VideoTooLong:
            case CreationToolError.VideoNeedsTrim:
                return `Error Too Long`
            case CreationToolError.VideoPrivate:
                return `Error Private`
            case CreationToolError.VideoLiveStream:
                return `Error Live Stream`
            case CreationToolError.UploadFailed:
            case CreationToolError.UploadFailedAgain:
                return `Error Upload Failed`
            case CreationToolError.NoSuperCutFiles:
                return `SuperCut - No Files Found`
            case CreationToolError.SuperCutUploadFailed:
                return `SuperCut - Upload Failed`
            case CreationToolError.SuperCutUpdateFailed:
                return `SuperCut - Unable to update Capture`
            case CreationToolError.SuperCutLoadFailed:
                return `SuperCut - Unable to Load Captures`
            case CreationToolError.TooManyFiles:
                return `Error Too Many Files`
            case CreationToolError.InvalidFontFile:
                return 'Error Invalid Font File'
            case CreationToolError.InvalidMOVFile:
                return 'Error Invalid MOV File'
            case CreationToolError.RemoveBackgroundFailed:
                return `Error Remove Background Failed`
        }
        return 'Error Code Not Found'
    }

    /**
     * Returns the error's description text.
     */
    export function description(error: CreationToolError): string {
        switch (error) {
            case CreationToolError.InvalidVideoFile:
                return `File must be ${getLabelFromMimeTypes(VIDEO_MIME_TYPES)}.`
            case CreationToolError.InvalidStillFile:
                return `File must be ${getLabelFromMimeTypes(STILL_MIME_TYPES)}.`
            case CreationToolError.InvalidAnimatedFile:
                return `File must be ${getLabelFromMimeTypes(ALL_ANIMATED_MIME_TYPES)}.`
            case CreationToolError.InvalidMediaFile:
                return `File must be ${getLabelFromMimeTypes(ALL_MIME_TYPES)}`
            case CreationToolError.InvalidFontFile:
                return `File must be ${getLabelFromMimeTypes(FONT_MIME_TYPES)}`
            case CreationToolError.InvalidVideoUrl:
                return `We were unable to obtain enough information from this video URL.`
            case CreationToolError.VideoTooShort:
                return `Clips must be at least ${MIN_OUTPUT_VIDEO_DURATION} second long.`
            case CreationToolError.VideoTooLong:
            case CreationToolError.FileSizeTooLarge:
                return `GIFs & Videos have to be smaller than ${fileSizeToDisplayText(
                    MAX_FILE_SIZE
                )} and shorter than ${secondsToDisplayText(MAX_FILE_DURATION)}.`
            case CreationToolError.VideoNeedsTrim:
                return `GIFs & Clips must be ${secondsToDisplayText(MAX_OUTPUT_VIDEO_DURATION)} or shorter.`
            case CreationToolError.VideoPrivate:
                return `The owner of this video does not allow us to make GIFs.`
            case CreationToolError.VideoLiveStream:
                return `This video appears to have no duration and may be a live stream. This is not yet supported.`
            case CreationToolError.VideoTrimError:
                return `An error occurred while attempting to trim your video.`
            case CreationToolError.VideoWidthTooSmall:
                return `Clips must be at least ${MIN_OUTPUT_VIDEO_DIMENSION} pixels wide.`
            case CreationToolError.VideoHeightTooSmall:
                return `Clips must be at least ${MIN_OUTPUT_VIDEO_DIMENSION} pixels tall.`
            case CreationToolError.VideoAspectRatioTooBig:
                return `Clips must have a maximum aspect ratio of ${MAX_OUTPUT_VIDEO_ASPECT_RATIO}`
            case CreationToolError.VideoAspectRatioTooSmall:
                return `Clips must have a minimum aspect ratio of ${MIN_OUTPUT_VIDEO_ASPECT_RATIO}.`
            case CreationToolError.NoSuperCutFiles:
                return `We were unable to find any Super Cut files.`
            case CreationToolError.NoAudio:
                return `Audio is required to upload as a ${CreationToolOutputType.title(CreationToolOutputType.Video)}.`
            case CreationToolError.NoTransparency:
                return `Stickers must have transparent backgrounds.`
            case CreationToolError.NotAnimated:
                return `File must be animated.`
            case CreationToolError.UploadFailed:
            case CreationToolError.UploadFailedAgain:
            case CreationToolError.SuperCutUploadFailed:
                return `An error occurred when uploading your file.`
            case CreationToolError.SuperCutUpdateFailed:
                return `An error occurred when updating your capture.`
            case CreationToolError.SuperCutLoadFailed:
                return `An error occurred when attempting to load your captures.`
            case CreationToolError.TooManyFiles:
                return `Only ${MAX_FILE_COUNT} files or less are supported at one time.`
            case CreationToolError.InvalidMOVFile:
                return `Certain types of .mov files aren't support on this browser, you can try Safari.`
            case CreationToolError.CaptionDownloadFailed:
                return `An error occured while fetching this video's captions. You may need to choose a different segmentation method.`
            case CreationToolError.RemoveBackgroundFailed:
                return `An error occured while removing the background from this file.`
        }
    }

    /**
     * Returns the error's fix.
     */
    export function fix(error: CreationToolError): CreationToolErrorFix {
        switch (error) {
            case CreationToolError.NoAudio:
            case CreationToolError.NoTransparency:
            case CreationToolError.VideoTooShort:
            case CreationToolError.VideoWidthTooSmall:
            case CreationToolError.VideoHeightTooSmall:
            case CreationToolError.VideoAspectRatioTooSmall:
            case CreationToolError.VideoAspectRatioTooBig:
            case CreationToolError.InvalidVideoFile:
                return CreationToolErrorFix.UploadAsGif
            case CreationToolError.VideoTooLong:
                return CreationToolErrorFix.Supercut
            case CreationToolError.VideoNeedsTrim:
                return CreationToolErrorFix.TrimVideo
            case CreationToolError.UploadFailed:
                return CreationToolErrorFix.RetryUpload
            default:
                return CreationToolErrorFix.SupportCenter
        }
    }

    /**
     * Returns the error's sticker id.
     */
    export function stickerId(error: CreationToolError): string {
        switch (error) {
            case CreationToolError.NoSuperCutFiles:
            case CreationToolError.VideoTooLong:
            case CreationToolError.VideoNeedsTrim:
                return 'g2iPmjRwOtrPYj34fl' // supercut scissors
            case CreationToolError.NoAudio:
                return 'USUIWSteF8DJoc5Snd' // thinking emoji
            case CreationToolError.InvalidAnimatedFile:
            case CreationToolError.InvalidStillFile:
            case CreationToolError.InvalidVideoFile:
            case CreationToolError.InvalidVideoUrl:
            case CreationToolError.InvalidMediaFile:
                return 'iD6QiXTTAYrU5C3c89' // finger wag
            case CreationToolError.FileSizeTooLarge:
            case CreationToolError.TooManyFiles:
            case CreationToolError.VideoTooShort:
            case CreationToolError.VideoWidthTooSmall:
            case CreationToolError.VideoHeightTooSmall:
            case CreationToolError.VideoAspectRatioTooSmall:
            case CreationToolError.VideoAspectRatioTooBig:
                return 'Iblv3w6yvj9DTMwCsJ' // small fingers
            default:
                return 'WprjTWyCWtfbJ11WEM' // gasp emoji
        }
    }

    /**
     * Returns an error from a server response.
     */
    export function fromServerResponse(error: Error): CreationToolError {
        const message = (error.message || '').toLowerCase()

        if (!message) return CreationToolError.UploadFailed

        // not animated
        if (message.indexOf('must be animated') > -1) {
            return CreationToolError.NotAnimated
        }

        // video too long
        if (message.indexOf('video duration must be <') > -1) {
            return CreationToolError.VideoTooLong
        }

        // video width too small
        if (message.indexOf('video width must be') > -1) {
            return CreationToolError.VideoWidthTooSmall
        }

        // video height too small
        if (message.indexOf('video height must be') > -1) {
            return CreationToolError.VideoHeightTooSmall
        }

        // video aspect ratio too big
        if (message.indexOf('video aspect ratio must be <=') > -1) {
            return CreationToolError.VideoAspectRatioTooBig
        }

        // video aspect ratio too small
        if (message.indexOf('video aspect ratio must be >=') > -1) {
            return CreationToolError.VideoAspectRatioTooSmall
        }

        return CreationToolError.UploadFailed
    }
}

export default CreationToolError
